import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  ThemeProvider,
  createGlobalStyle,
  type DefaultTheme,
} from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import { GlobalTypographyStyle } from '@azuga-bms/theme';

import { useAppSelector } from 'store/hooks';
import { selectApp } from 'app/pages/Dashboard/slice/dashboardSlice';
import {
  buildPortalAdsTheme,
  resolveActiveAccountThemeId,
} from 'app/utils/ads-theme';

const GlobalStyle = GlobalTypographyStyle();

const AdsTypographyOverrides = createGlobalStyle`
  :root {
    --ads-font-family: 'DM Sans', sans-serif;
    --ads-font-size-root: 14px;
    --ads-font-weight-body: 400;
    --ads-font-weight-bold: 500;
    --ads-font-weight-heading: 700;
    --ads-line-height-body: 1.25;
    --ads-line-height-label: 1.5;
    --ads-line-height-heading: 1.125;
    --ads-letter-spacing: normal;
  }

  html,
  body,
  #root,
  button,
  input,
  select,
  textarea,
  optgroup,
  .p-component,
  .p-inputtext,
  .p-dropdown,
  .p-multiselect,
  .p-calendar,
  .p-datatable,
  .p-dialog,
  .p-toast,
  .Toastify,
  [class^='azg-'],
  [class*=' azg-'] {
    font-family: var(--ads-font-family) !important;
    letter-spacing: var(--ads-letter-spacing);
  }

  body {
    font-size: var(--ads-font-size-root);
    font-weight: var(--ads-font-weight-body);
    line-height: var(--ads-line-height-body);
    letter-spacing: var(--ads-letter-spacing);
    color: #171d16;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--ads-font-family);
    font-weight: var(--ads-font-weight-heading);
    line-height: var(--ads-line-height-heading);
    letter-spacing: var(--ads-letter-spacing);
  }

  h1 {
    font-size: 32px;
  }

  h2,
  h2.azg-typography {
    font-size: 28px;
    letter-spacing: 0 !important;
  }

  .az-page-header h1,
  .az-page-header h2,
  .az-page-title,
  h1.az-page-title,
  h2.az-page-title,
  h2.azg-typography.az-page-title {
    font-family: var(--ads-font-family) !important;
    font-size: 20px !important;
    font-weight: 700 !important;
    line-height: normal !important;
    letter-spacing: 0 !important;
    color: #171d16 !important;
    margin-right: 10px;
  }

  .az-section-title,
  h3.az-section-title {
    font-family: var(--ads-font-family) !important;
    font-size: 18px !important;
    font-weight: 400 !important;
    line-height: normal !important;
    letter-spacing: normal !important;
    color: #171d16 !important;
    margin: 0;
  }

  h3 {
    font-size: 24px;
  }

  h4 {
    font-size: 20px;
  }

  h5 {
    font-size: 16px;
  }

  h6 {
    font-size: 14px;
  }

  label,
  .p-float-label > label {
    font-family: var(--ads-font-family);
    font-size: 16px;
    font-weight: var(--ads-font-weight-body);
    line-height: var(--ads-line-height-label);
    letter-spacing: var(--ads-letter-spacing);
  }

  button,
  .p-button,
  .p-button .p-button-label {
    font-family: var(--ads-font-family);
    font-size: 14px;
    font-weight: var(--ads-font-weight-body);
    line-height: var(--ads-line-height-label);
    letter-spacing: var(--ads-letter-spacing);
  }

  input,
  select,
  textarea,
  .p-inputtext,
  .p-dropdown-label,
  .p-multiselect-label,
  .p-dropdown-item,
  .p-multiselect-item {
    font-family: var(--ads-font-family);
    font-size: 14px;
    font-weight: var(--ads-font-weight-body);
    line-height: var(--ads-line-height-body);
    letter-spacing: var(--ads-letter-spacing);
  }

  .rdt_Table {
    font-family: var(--ads-font-family);
    font-size: 14px;
    font-weight: var(--ads-font-weight-body);
    letter-spacing: var(--ads-letter-spacing);
    line-height: var(--ads-line-height-body);
  }

  .rdt_TableHead,
  .rdt_TableCol {
    color: #171d16 !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    letter-spacing: normal !important;
    text-transform: none !important;
  }

  .rdt_TableRow,
  .rdt_TableCell,
  .rdt_Pagination {
    color: #171d16;
    font-size: 14px;
    font-weight: var(--ads-font-weight-body);
    letter-spacing: var(--ads-letter-spacing);
  }

  /* ADS OverlayPanel fly-in — disable; keep menus above table/modals.
   * MultiSelect's styled OverlayPanel hardcodes z-index: 999999999 (below
   * AdsModal). Force max layer on portaled and in-DOM menus. */
  .azg-overlay-panel,
  .azg-ms-dropdown,
  .azg-ms-dropdown.azg-overlay-panel,
  .az-ads-filter-dropdown,
  body > .azg-overlay-panel,
  body > .azg-ms-dropdown,
  [data-testid='OverlayPanel'] {
    transition: none !important;
    transform: none !important;
    z-index: 2147483647 !important;
  }

  .azg-select .azg-menu-list,
  [data-testid='MenuList'] {
    z-index: 2147483647 !important;
  }

  .p-datatable-thead,
  .p-datatable .p-datatable-thead > tr > th {
    font-family: var(--ads-font-family);
    font-size: 14px;
    font-weight: var(--ads-font-weight-heading);
    letter-spacing: var(--ads-letter-spacing);
    line-height: var(--ads-line-height-body);
  }

  .p-datatable-tbody,
  .p-datatable .p-datatable-tbody > tr > td,
  .p-datatable-table .p-datatable-tbody > tr > td {
    font-family: var(--ads-font-family);
    font-size: 14px;
    font-weight: var(--ads-font-weight-body);
    letter-spacing: var(--ads-letter-spacing);
    line-height: var(--ads-line-height-body);
  }
`;

type AzugaThemeProviderProps = {
  children: ReactNode;
};

const selectThemeColor = createSelector(
  [selectApp],
  state => state.themeColor,
);

/**
 * Watches body `data-account-theme` so ADS updates on preview/apply without
 * waiting for Redux (Account Settings theme preview uses applyAccountTheme only).
 */
function useAccountThemeIdForAds(): string {
  const themeColor = useAppSelector(selectThemeColor);
  const [bodyTheme, setBodyTheme] = useState(
    () =>
      (typeof document !== 'undefined'
        ? document.body.dataset.accountTheme
        : undefined) ?? '',
  );

  useEffect(() => {
    const sync = () => {
      setBodyTheme(document.body.dataset.accountTheme ?? '');
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'data-account-theme'],
    });

    return () => observer.disconnect();
  }, []);

  return resolveActiveAccountThemeId(bodyTheme || themeColor);
}

/**
 * Wraps the app with azuga-design-system theme (styled-components).
 * Primary/link colors track portal account themes (same as header/sidebar).
 */
export function AzugaThemeProvider({ children }: AzugaThemeProviderProps) {
  const accountThemeId = useAccountThemeIdForAds();
  const theme = useMemo(
    () => buildPortalAdsTheme(accountThemeId) as unknown as DefaultTheme,
    [accountThemeId],
  );

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AdsTypographyOverrides />
      {children}
    </ThemeProvider>
  );
}
